/**
 * Rules keyed by pairId for row binding, shared by the Entry (opener) and
 * Unwinding pages. A pair is supposed to have at most ONE rule per rule
 * type, but a duplicate-create race has produced orphan duplicates in
 * prod. The old maps were last-write-wins over a newest-first list, so
 * rows silently bound to the OLDEST rule. When duplicates exist, prefer
 * the ACTIVE rule (mirrors `anyRulesByPairId` in EntryPage) and warn once
 * per pair so the orphan is visible instead of swallowed.
 */

export interface BuildRulesByPairIdOptions {
  /** Page label for the duplicate warning ('entry' | 'unwinding'). */
  scope?: string;
  /** Warn-once registry — injectable for tests. Defaults to a module-level
   *  set keyed by `${scope}:${pairId}` so one page's warning never masks
   *  the other's for the same pair. */
  warned?: Set<string>;
}

const defaultWarned = new Set<string>();

export function buildRulesByPairId<
  T extends { _id: string; pairId?: string | null; status?: string }
>(rules: T[] | undefined, opts: BuildRulesByPairIdOptions = {}): Map<string, T> {
  const scope = opts.scope ?? 'entry';
  const warned = opts.warned ?? defaultWarned;
  const map = new Map<string, T>();
  for (const rule of rules ?? []) {
    if (!rule.pairId) continue;
    const existing = map.get(rule.pairId);
    if (!existing) {
      map.set(rule.pairId, rule);
      continue;
    }
    const warnKey = `${scope}:${rule.pairId}`;
    if (!warned.has(warnKey)) {
      warned.add(warnKey);
      console.warn(
        `[${scope}] pair ${rule.pairId} has duplicate ${scope} rules ` +
          `(${existing._id}, ${rule._id}) — binding the active one`
      );
    }
    if (rule.status === 'active' && existing.status !== 'active') {
      map.set(rule.pairId, rule);
    }
  }
  return map;
}
